'use client';

import { getStoryblokApi } from '@storyblok/react';
import { usePathname } from 'next/navigation';
import { createContext, useEffect, useMemo, useState } from 'react';

export function hasTags(filterTags = [], postTags = []) {
  if (filterTags.length === 0) return true;
  return filterTags.some((t) => postTags.includes(t));
}

export const SORT_ORDER = {
  'Heavy Civil Construction': 0,
  'Oil & Gas Construction': 1,
  'Mine Development': 2,
  'Environment & Demolition': 3,
};

export function sortTags(a, b) {
  if (
    Object.prototype.hasOwnProperty.call(SORT_ORDER, a) &&
    Object.prototype.hasOwnProperty.call(SORT_ORDER, b)
  ) {
    return SORT_ORDER[a] - SORT_ORDER[b];
  }
  return 0;
}

export function sortArticles(a, b) {
  return sortTags(a.content.tags[0], b.content.tags[0]);
}

export type ArticleContextType = {
  loading: boolean;
  articles: any[];
  tags: any[];
  selectedTags: string[];
  toggleTag: (tag: string) => void;
  clearTags: () => void;
};

export const ArticleContext = createContext<ArticleContextType>({
  loading: false,
  articles: [],
  tags: [],
  selectedTags: [],
  toggleTag: () => {},
  clearTags: () => {},
});

export function useArticles(type = 'project'): ArticleContextType {
  const [articles, setArticles] = useState([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const pathname = usePathname();

  function toggleTag(tag) {
    setSelectedTags((prev) => {
      let newTags = [...prev];
      if (!newTags.includes(tag)) {
        newTags = [];
        newTags.push(tag);
      } else {
        const index = newTags.indexOf(tag);
        newTags.splice(index, 1);
      }
      return newTags;
    });
  }

  function clearTags() {
    setSelectedTags([]);
  }

  const filteredArticles = articles.filter((article) => {
    const has = hasTags(selectedTags, article.content.tags);
    return has;
  });

  useEffect(() => {
    setLoading(true);
    setArticles([]);
    setSelectedTags([]);
    // Story posts (Newsroom) are listed newest first; projects keep the category order
    const isStoryPost = type === 'storyPost';
    const getArticles = async () => {
      const storyblokApi = getStoryblokApi();
      const { data } = await storyblokApi.get(`cdn/stories`, {
        version:
          process.env.NEXT_PUBLIC_STORYBLOK_VERSION === 'published'
            ? 'published'
            : 'draft',
        content_type: type,
        resolve_relations: ['storyPost.tags'],
        per_page: 100,
        ...(isStoryPost && { sort_by: 'first_published_at:desc' }),
      });
      const stories = data.stories.map((article) => {
        article.content.tags = article.content.tags
          .map((t) => {
            if (typeof t === 'string') return t;
            return t.name;
          })
          .sort(sortTags);
        article.content.slug = article.slug;
        return article;
      });
      setArticles(() => (isStoryPost ? stories : stories.sort(sortArticles)));
      setLoading(false);
    };
    getArticles();

    return () => {
      setLoading(false);
      setArticles([]);
      setSelectedTags([]);
    };
  }, [type, pathname]);

  const tags = useMemo(() => {
    return Array.from(
      articles.reduce((a, b) => {
        b.content.tags.forEach((t: string) => a.add(t));
        return a;
      }, new Set<string>())
    ).sort(sortTags);
  }, [articles]);

  return {
    loading,
    articles: filteredArticles,
    tags,
    selectedTags,
    toggleTag,
    clearTags,
  };
}
